import React from 'react';
import { Box, Button, CircularProgress, Grid, IconButton, Paper, Typography, Alert } from '@mui/material';
import { CloudUpload, Delete, Visibility, CheckCircle, Info as InfoIcon } from '@mui/icons-material';

/**
 * A reusable document upload field component that handles:
 * - File selection
 * - Processing status display
 * - UI locking during processing
 * - Preview display
 * 
 * @param {Object} props Component props
 * @param {string} props.documentType The type of document (e.g., 'photo', 'emiratesId', 'passportCopy')
 * @param {string} props.label Display label for the document field
 * @param {Function} props.onFileSelect Callback when a file is selected
 * @param {Object} props.file The selected file object
 * @param {string} props.previewUrl URL for the document preview
 * @param {boolean} props.isProcessing Whether the document is currently being processed
 * @param {Object} props.processingStatus Status object with status and message properties
 * @param {Function} props.onRemove Callback when the file is removed
 * @param {Function} props.onView Callback when the preview button is clicked
 * @param {string} props.error Error message if any
 * @param {Array} props.acceptedTypes Array of accepted file types (e.g., ['image/*', 'application/pdf'])
 * @param {string} props.description Description text for the upload field
 * @param {boolean} props.required Whether the document is required
 * @param {Object} props.customStyles Custom styling options for various parts of the component
 */
const DocumentUploadField = ({
  documentType,
  label,
  onFileSelect,
  file,
  previewUrl,
  isProcessing,
  processingStatus,
  onRemove,
  onView,
  error,
  acceptedTypes = ['image/*', 'application/pdf'],
  description = '',
  required = false,
  customStyles = {},
  showPreviewImage = true,
  fileNameFontSize = '0.95rem'
}) => {
  // Create a unique ID for the file input
  const inputId = `${documentType}-upload-input`;
  
  // Determine if we have a meaningful file (object URL, actual File, backend metadata, or at least a preview URL)
  const hasFileMeta = !!(file && (file.file || file.filePath || file.fileName || file.name));
  const hasFile = hasFileMeta || !!previewUrl;
  // Debug: log computed hasFile and minimal file metadata
  try {
    if (process && process.env && process.env.NODE_ENV !== 'production') {
      // Keep logs concise
      // eslint-disable-next-line no-console
      console.debug('[DocumentUploadField] hasFile:', hasFile, 'type:', documentType, 'name:', file?.name || file?.fileName, 'path?', !!file?.filePath);
    }
  } catch (_) { /* no-op */ }
  
  // Determine status color and icon
  const getStatusColor = () => {
    if (!processingStatus) return 'default';
    switch (processingStatus.status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'timeout': return 'warning';
      case 'processing': return 'info';
      default: return 'default';
    }
  };
  
  const getStatusIcon = () => {
    if (!processingStatus) return null;
    switch (processingStatus.status) {
      case 'success': return <CheckCircle fontSize="small" />;
      case 'error': return <InfoIcon fontSize="small" color="error" />;
      case 'timeout': return <InfoIcon fontSize="small" color="warning" />;
      case 'processing': return <CircularProgress size={16} />;
      default: return null;
    }
  };
  
  // Merge default styles with custom styles
  const containerStyles = { p: 1, mb: 1, ...customStyles?.container };
  const previewBoxStyles = { 
    border: '1px solid #e0e0e0', 
    borderRadius: 1, 
    p: 0.75, 
    ...customStyles?.previewBox 
  };
  const uploadBoxStyles = { ...customStyles?.uploadBox };
  
  // Button styling and props
  const uploadButtonProps = {
    variant: customStyles?.uploadButton?.variant || "outlined",
    color: customStyles?.uploadButton?.color || "primary",
    size: customStyles?.uploadButton?.size || "small",
    startIcon: <CloudUpload />,
    disabled: isProcessing,
    fullWidth: !customStyles?.uploadButton?.fullWidth === false,
    sx: {
      color: customStyles?.uploadButton?.variant === "contained" ? "#fff" : 'primary.main',
      borderColor: 'primary.main',
      textTransform: 'none',
      fontWeight: 600,
      '&.Mui-disabled': { color: 'text.secondary', borderColor: 'divider', opacity: 1 },
      ...customStyles?.uploadButton?.sx || {}
    }
  };
  
  const removeButtonProps = {
    variant: customStyles?.removeButton?.variant || "outlined",
    color: customStyles?.removeButton?.color || "error",
    size: customStyles?.removeButton?.size || "small",
    startIcon: <Delete />,
    disabled: isProcessing,
    sx: {
      color: customStyles?.removeButton?.variant === "contained" ? "#fff" : "error.main",
      textTransform: 'none',
      ...customStyles?.removeButton?.sx || {}
    }
  };
  
  const uploadButtonLabel = customStyles?.uploadButton?.label || (label ? `Upload ${label}` : 'Upload');
  const removeButtonLabel = customStyles?.removeButton?.label || "Remove";

  // Wrap handlers to add debug logs without changing parent APIs
  const handleSelect = (e) => {
    try {
      // eslint-disable-next-line no-console
      console.debug('[DocumentUploadField] onFileSelect for', documentType, 'file:', e?.target?.files?.[0]?.name);
    } catch (_) {}
    if (typeof onFileSelect === 'function') onFileSelect(e);
  };

  const handleRemove = () => {
    try {
      // eslint-disable-next-line no-console
      console.debug('[DocumentUploadField] onRemove for', documentType, 'file:', file?.name || file?.fileName);
    } catch (_) {}
    if (typeof onRemove === 'function') onRemove();
  };

  const handleView = () => {
    try {
      // eslint-disable-next-line no-console
      console.debug('[DocumentUploadField] onView for', documentType, 'file:', file?.name || file?.fileName);
    } catch (_) {}
    if (typeof onView === 'function') onView();
  };

  return (
    <Paper elevation={0} sx={containerStyles}>
      {label && (
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ fontSize: '0.95rem' }}>
              {label} {required && <Typography component="span" color="error">*</Typography>}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {/* Content will be here */}
          </Grid>
        </Grid>
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {/* File upload button or preview */}
        {!hasFile ? (
          <Box sx={uploadBoxStyles}>
            <input
              accept={acceptedTypes.join(',')}
              id={inputId}
              type="file"
              onChange={handleSelect}
              style={{ display: 'none' }}
              disabled={isProcessing}
            />
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                {description}
              </Typography>
            )}
            <label htmlFor={inputId}>
              <Button
                {...uploadButtonProps}
                component="span"
              >
                {uploadButtonLabel}
                {isProcessing && (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
              </Button>
            </label>
          </Box>
        ) : (
          <Box sx={{ ...previewBoxStyles, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start', gap: 2 }}>
            {previewUrl && showPreviewImage && (
              <Box 
                sx={{ 
                  width: { xs: '100%', sm: 140 }, 
                  height: 120, 
                  backgroundImage: `url(${previewUrl})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  flexShrink: 0,
                  ...customStyles?.previewImage
                }} 
              />
            )}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontSize: fileNameFontSize || '0.9rem' }}>
                {(() => {
                  const displayName = file?.name || file?.fileName || (previewUrl ? (label || documentType || 'Document') : 'Document');
                  const sizeBytes = typeof file?.size === 'number' ? file.size : (typeof file?.fileSize === 'number' ? file.fileSize : null);
                  const sizeText = sizeBytes != null ? ` (${(sizeBytes / 1024).toFixed(2)} KB)` : '';
                  return `${displayName}${sizeText}`;
                })()}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                {typeof onView === 'function' && (
                  <Button 
                    variant="outlined"
                    size="small" 
                    onClick={handleView}
                    disabled={isProcessing}
                    startIcon={<Visibility />}
                    sx={{ color: 'primary.main', textTransform: 'none' }}
                  >
                    View
                  </Button>
                )}
                <Button 
                  {...removeButtonProps}
                  onClick={handleRemove}
                >
                  {removeButtonLabel}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
            
        {/* Processing indicator (compact, inline). Show only when a file is present to avoid wide bars. */}
        {hasFile && isProcessing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 0.25 }}>
            <CircularProgress size={16} color="primary" />
            <Typography variant="caption" color="text.secondary">
              {processingStatus?.message || `Processing ${label || documentType}...`}
            </Typography>
          </Box>
        )}
        
        {/* Status message (hide during processing to avoid duplicate rows) */}
        {processingStatus && processingStatus.status !== 'processing' && (
          <Alert 
            severity={getStatusColor()} 
            icon={getStatusIcon()}
            sx={{ py: 0.5, px: 1.5, mt: 0.5 }}
          >
            <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
              {processingStatus.message}
            </Typography>
          </Alert>
        )}
        
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mt: 0.5, py: 0.5, px: 1.5 }}>
            <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
              {error}
            </Typography>
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default DocumentUploadField;
